import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing OAuth callback...');

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');

        if (error) {
          setStatus('error');
          setMessage(`OAuth error: ${error}`);
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('No authorization code received');
          return;
        }

        setMessage('Exchanging authorization code for tokens...');

        // Call the backend API to exchange the code for tokens
        const response = await fetch(`${API}/oauth/exchange-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });

        if (!response.ok) {
          throw new Error(`Token exchange failed: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.status === 'success') {
          setStatus('success');
          setMessage('OAuth connection successful! Redirecting...');
          
          // Redirect based on user type
          setTimeout(() => {
            navigate(result.redirect);
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Token exchange failed');
        }

      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setMessage(`Error: ${err.message}`);
      }
    };

    processOAuthCallback();
  }, [searchParams, navigate]);

  const retryOAuth = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Connecting to GoHighLevel</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-500 text-6xl mb-6">✅</div>
              <h2 className="text-2xl font-bold text-green-600 mb-4">Connection Successful!</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-red-500 text-6xl mb-6">❌</div>
              <h2 className="text-2xl font-bold text-red-600 mb-4">Connection Failed</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <button
                onClick={retryOAuth}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
